import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, FileArrowUp, DownloadSimple, Printer, ClipboardText, CheckCircle, Warning } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import api, { formatIDR, formatDate, formatApiError } from "../lib/api";
import { generatePDF } from "../lib/pdf";

const STATUS_META = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-700" },
  approved: { label: "Disetujui", cls: "bg-blue-100 text-blue-700" },
  ongoing: { label: "Berjalan", cls: "bg-amber-100 text-amber-700" },
  done: { label: "Selesai", cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Ditolak", cls: "bg-rose-100 text-rose-700" },
};

const EMPTY = { title: "", lembaga_id: "", nomenklatur_source: "", amount_proposal: 0, amount_realisasi: 0, date_start: "", date_end: "", status: "draft", notes: "", lpj_summary: "" };

export default function KegiatanLPJ() {
  const [items, setItems] = useState([]);
  const [rekap, setRekap] = useState(null);
  const [lembagas, setLembagas] = useState([]);
  const [nomens, setNomens] = useState([]);
  const [filterLembaga, setFilterLembaga] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterNomen, setFilterNomen] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const propRef = useRef();
  const lpjRef = useRef();

  const load = async () => {
    const q = new URLSearchParams();
    if (filterLembaga !== "all") q.set("lembaga_id", filterLembaga);
    if (filterStatus !== "all") q.set("status", filterStatus);
    if (filterNomen !== "all") q.set("nomenklatur_source", filterNomen);
    const [k, r, l, n] = await Promise.all([
      api.get(`/kegiatan${q.toString() ? "?" + q : ""}`),
      api.get(`/kegiatan/rekap${q.toString() ? "?" + q : ""}`),
      api.get("/lembaga").catch(() => ({ data: [] })),
      api.get("/nomenklatur").catch(() => ({ data: [] })),
    ]);
    setItems(k.data); setRekap(r.data); setLembagas(l.data); setNomens(n.data);
  };
  useEffect(() => { load(); }, [filterLembaga, filterStatus, filterNomen]); // eslint-disable-line

  const lembagaMap = Object.fromEntries(lembagas.map((l) => [l.id, l.name]));
  const nomenMap = Object.fromEntries(nomens.map((n) => [n.code, n.name]));

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, lembaga_id: lembagas[0]?.id || "", nomenklatur_source: nomens[0]?.code || "" }); setOpen(true); };
  const openEdit = (k) => { setEditing(k); setForm({ ...EMPTY, ...k }); setOpen(true); };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Judul kegiatan wajib diisi");
    if (!form.lembaga_id) return toast.error("Lembaga wajib dipilih");
    if (!form.nomenklatur_source) return toast.error("Sumber dana wajib dipilih");
    try {
      const payload = { ...form, amount_proposal: Number(form.amount_proposal || 0), amount_realisasi: Number(form.amount_realisasi || 0) };
      // Strip file fields (upload via separate endpoint)
      delete payload.proposal_file_data; delete payload.proposal_file_name;
      delete payload.lpj_file_data; delete payload.lpj_file_name;
      delete payload.has_proposal; delete payload.has_lpj;
      if (editing) await api.put(`/kegiatan/${editing.id}`, payload);
      else await api.post("/kegiatan", payload);
      toast.success("Kegiatan tersimpan"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus kegiatan ini beserta filenya?")) return;
    try { await api.delete(`/kegiatan/${id}`); toast.success("Terhapus"); load(); }
    catch { toast.error("Gagal menghapus"); }
  };

  const uploadFile = async (kid, ftype, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("File maksimal 5MB");
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = String(reader.result).split(",")[1];
      try {
        await api.post(`/kegiatan/${kid}/upload-${ftype}`, { file_name: file.name, file_data: base64 });
        toast.success(`File ${ftype} terupload`);
        load();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal upload"); }
    };
    reader.readAsDataURL(file);
  };

  const downloadFile = async (kid, ftype) => {
    try {
      const { data } = await api.get(`/kegiatan/${kid}/file/${ftype}`);
      const link = document.createElement("a");
      link.href = `data:application/octet-stream;base64,${data.file_data}`;
      link.download = data.file_name || `${ftype}.pdf`;
      link.click();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal download"); }
  };

  const printRekap = () => {
    if (!rekap) return;
    generatePDF({
      title: "Rekap Kegiatan & LPJ", subtitle: filterLembaga === "all" ? "Seluruh Lembaga" : (lembagaMap[filterLembaga] || "-"),
      headers: ["Judul", "Lembaga", "Sumber Dana", "Anggaran", "Realisasi", "Sisa", "Status", "Proposal", "LPJ"],
      rows: rekap.rows.map((k) => [k.title, k.lembaga_name || "-", `${k.nomenklatur_source}${k.nomenklatur_name ? " – " + k.nomenklatur_name : ""}`, formatIDR(k.amount_proposal), formatIDR(k.amount_realisasi), formatIDR(k.amount_proposal - k.amount_realisasi), STATUS_META[k.status]?.label || k.status, k.has_proposal ? "✓" : "-", k.has_lpj ? "✓" : "-"]),
      meta: {
        "Total Kegiatan": rekap.count, "Total Anggaran": formatIDR(rekap.total_proposal),
        "Total Realisasi": formatIDR(rekap.total_realisasi), "Sisa Anggaran": formatIDR(rekap.sisa_anggaran),
        "Proposal Diupload": `${rekap.with_proposal} / ${rekap.count}`, "LPJ Diupload": `${rekap.with_lpj} / ${rekap.count}`,
      },
      filename: `rekap-kegiatan-lpj.pdf`,
    });
  };

  return (
    <div data-testid="kegiatan-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#0B8D95] font-semibold mb-1">Keuangan Internal</div>
          <h1 className="font-display text-3xl font-black text-foreground tracking-tight">Proposal & LPJ</h1>
          <p className="text-muted-foreground text-sm mt-1">Admin lembaga upload proposal & LPJ per kegiatan. Sumber dana otomatis mempengaruhi realisasi pos.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={printRekap} data-testid="kegiatan-print-rekap"><Printer size={14} className="mr-1.5" />Cetak Rekap</Button>
          <Button size="sm" onClick={openNew} data-testid="kegiatan-add-btn"><Plus size={14} className="mr-1.5" />Tambah Kegiatan</Button>
        </div>
      </div>

      {/* Rekap cards */}
      {rekap && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <StatCard label="Total Kegiatan" value={rekap.count} icon={ClipboardText} />
          <StatCard label="Total Anggaran" value={formatIDR(rekap.total_proposal)} tone="info" />
          <StatCard label="Realisasi" value={formatIDR(rekap.total_realisasi)} tone="ok" />
          <StatCard label="Sisa Anggaran" value={formatIDR(rekap.sisa_anggaran)} tone={rekap.sisa_anggaran < 0 ? "danger" : "default"} />
        </div>
      )}

      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-3 mb-4 flex flex-wrap gap-3 items-end">
        <div><Label className="text-xs">Lembaga</Label>
          <Select value={filterLembaga} onValueChange={setFilterLembaga}>
            <SelectTrigger className="w-48" data-testid="kegiatan-filter-lembaga"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua Lembaga</SelectItem>{lembagas.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Status</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-36" data-testid="kegiatan-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua</SelectItem>{Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Sumber Dana</Label>
          <Select value={filterNomen} onValueChange={setFilterNomen}>
            <SelectTrigger className="w-44" data-testid="kegiatan-filter-nomen"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua Pos</SelectItem>{nomens.map((n) => <SelectItem key={n.code} value={n.code}>{n.code} – {n.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-3 font-bold">Kegiatan</th>
                <th className="px-3 py-3 font-bold">Lembaga</th>
                <th className="px-3 py-3 font-bold">Sumber Dana</th>
                <th className="px-3 py-3 font-bold">Tanggal</th>
                <th className="px-3 py-3 font-bold text-right">Anggaran</th>
                <th className="px-3 py-3 font-bold text-right">Realisasi</th>
                <th className="px-3 py-3 font-bold text-center">Status</th>
                <th className="px-3 py-3 font-bold text-center">Proposal</th>
                <th className="px-3 py-3 font-bold text-center">LPJ</th>
                <th className="px-3 py-3 font-bold text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {items.map((k) => (
                <tr key={k.id} className="border-t border-border hover:bg-muted/30" data-testid={`kegiatan-row-${k.id}`}>
                  <td className="px-3 py-2.5"><div className="font-bold">{k.title}</div>{k.notes && <div className="text-xs text-muted-foreground line-clamp-1">{k.notes}</div>}</td>
                  <td className="px-3 py-2.5 text-xs">{lembagaMap[k.lembaga_id] || "-"}</td>
                  <td className="px-3 py-2.5 text-xs"><div className="font-bold">{k.nomenklatur_source}</div><div className="text-muted-foreground">{nomenMap[k.nomenklatur_source] || ""}</div></td>
                  <td className="px-3 py-2.5 text-xs">{k.date_start ? formatDate(k.date_start) : "-"}{k.date_end ? ` → ${formatDate(k.date_end)}` : ""}</td>
                  <td className="px-3 py-2.5 text-right font-mono font-bold tabular-nums">{formatIDR(k.amount_proposal)}</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums text-emerald-600">{formatIDR(k.amount_realisasi)}</td>
                  <td className="px-3 py-2.5 text-center"><span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-black ${STATUS_META[k.status]?.cls}`}>{STATUS_META[k.status]?.label}</span></td>
                  <td className="px-3 py-2.5 text-center">
                    {k.has_proposal ? (
                      <button onClick={() => downloadFile(k.id, "proposal")} className="inline-flex items-center gap-1 text-emerald-600 text-xs font-bold hover:underline" data-testid={`kegiatan-dl-proposal-${k.id}`}><CheckCircle size={12} weight="fill" /><DownloadSimple size={12} /></button>
                    ) : (
                      <>
                        <input type="file" hidden ref={(el) => { if (el) propRef.current = { ...(propRef.current || {}), [k.id]: el }; }} onChange={(e) => e.target.files?.[0] && uploadFile(k.id, "proposal", e.target.files[0])} accept=".pdf,.doc,.docx,.jpg,.png" data-testid={`kegiatan-file-proposal-${k.id}`} />
                        <button onClick={() => propRef.current?.[k.id]?.click()} className="inline-flex items-center gap-1 text-muted-foreground text-xs hover:text-foreground" title="Upload Proposal"><FileArrowUp size={13} /></button>
                      </>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {k.has_lpj ? (
                      <button onClick={() => downloadFile(k.id, "lpj")} className="inline-flex items-center gap-1 text-emerald-600 text-xs font-bold hover:underline" data-testid={`kegiatan-dl-lpj-${k.id}`}><CheckCircle size={12} weight="fill" /><DownloadSimple size={12} /></button>
                    ) : (
                      <>
                        <input type="file" hidden ref={(el) => { if (el) lpjRef.current = { ...(lpjRef.current || {}), [k.id]: el }; }} onChange={(e) => e.target.files?.[0] && uploadFile(k.id, "lpj", e.target.files[0])} accept=".pdf,.doc,.docx,.jpg,.png" data-testid={`kegiatan-file-lpj-${k.id}`} />
                        <button onClick={() => lpjRef.current?.[k.id]?.click()} className={`inline-flex items-center gap-1 text-xs ${k.status === "done" ? "text-rose-500" : "text-muted-foreground"} hover:text-foreground`} title="Upload LPJ">
                          {k.status === "done" ? <Warning size={13} weight="fill" /> : <FileArrowUp size={13} />}
                        </button>
                      </>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(k)} className="p-1.5 hover:bg-muted rounded" data-testid={`kegiatan-edit-${k.id}`}><PencilSimple size={14} /></button>
                    <button onClick={() => remove(k.id)} className="p-1.5 hover:bg-rose-50 rounded text-rose-600" data-testid={`kegiatan-delete-${k.id}`}><Trash size={14} /></button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={10} className="px-5 py-16 text-center text-muted-foreground"><ClipboardText size={40} weight="duotone" className="mx-auto mb-2" /><div className="font-semibold">Belum ada kegiatan tercatat</div></td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="kegiatan-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit" : "Tambah"} Kegiatan</DialogTitle>
            <DialogDescription>Sumber dana dari pos keuangan (nomenklatur) akan mengurangi cashflow pos tersebut.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Judul Kegiatan *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="kegiatan-title-input" placeholder="Contoh: MPLS Kelas 7, Pelatihan Guru, ..." /></div>
            <div><Label>Lembaga *</Label>
              <Select value={form.lembaga_id} onValueChange={(v) => setForm({ ...form, lembaga_id: v })}>
                <SelectTrigger data-testid="kegiatan-lembaga-select"><SelectValue placeholder="Pilih lembaga" /></SelectTrigger>
                <SelectContent>{lembagas.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Sumber Dana (Pos) *</Label>
              <Select value={form.nomenklatur_source} onValueChange={(v) => setForm({ ...form, nomenklatur_source: v })}>
                <SelectTrigger data-testid="kegiatan-nomen-select"><SelectValue placeholder="Pilih pos keuangan" /></SelectTrigger>
                <SelectContent>{nomens.map((n) => <SelectItem key={n.code} value={n.code}>{n.code} – {n.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Tanggal Mulai</Label><Input type="date" value={form.date_start || ""} onChange={(e) => setForm({ ...form, date_start: e.target.value })} /></div>
            <div><Label>Tanggal Selesai</Label><Input type="date" value={form.date_end || ""} onChange={(e) => setForm({ ...form, date_end: e.target.value })} /></div>
            <div><Label>Anggaran (Rp)</Label><Input type="number" value={form.amount_proposal || 0} onChange={(e) => setForm({ ...form, amount_proposal: e.target.value })} data-testid="kegiatan-anggaran" /></div>
            <div><Label>Realisasi (Rp)</Label><Input type="number" value={form.amount_realisasi || 0} onChange={(e) => setForm({ ...form, amount_realisasi: e.target.value })} data-testid="kegiatan-realisasi" /></div>
            <div className="col-span-2"><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="kegiatan-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Catatan / Deskripsi</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <div className="col-span-2"><Label>Ringkasan LPJ (opsional)</Label><Textarea rows={2} value={form.lpj_summary || ""} onChange={(e) => setForm({ ...form, lpj_summary: e.target.value })} placeholder="Ringkasan hasil kegiatan setelah selesai" /></div>
            {editing && (
              <div className="col-span-2 pt-2 border-t border-border">
                <div className="text-xs text-muted-foreground mb-2">Upload file di kolom Proposal / LPJ pada tabel setelah simpan.</div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} data-testid="kegiatan-save-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, tone = "default", icon: Icon }) {
  const tones = { default: "bg-card border-border", info: "bg-blue-50 border-blue-200", ok: "bg-emerald-50 border-emerald-200", danger: "bg-rose-50 border-rose-200" };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{label}</div>
        {Icon && <Icon size={16} weight="duotone" className="text-foreground/60" />}
      </div>
      <div className="font-display text-xl md:text-2xl font-black text-foreground tracking-tight tabular-nums">{value}</div>
    </div>
  );
}
